import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { RuleProvider, useRule } from "../CreateRuleNew/RuleReducer";
import { useDispatch, useSelector } from "react-redux";
import { createAdvRuleApi } from "../../../../../Api Methods/Api";
import WizardStepTriggerType from "./WizardStepTriggerType";
import WizardStepTrigger from "./WizardStepTrigger";
import WizardStepConditions from "./WizardStepConditions";
import WizardStepRestrictions from "./WizardStepRestrictions";
import WizardStepAnythingElse from "./WizardStepAnythingElse";
import WizardStepDeadlineChecks from "./WizardStepDeadlineChecks";
import WizardStepDeadlineTimeframe from "./WizardStepDeadlineTimeframe";
import WizardStepDayTimeRestrictions from "./WizardStepDayTimeRestrictions";
import WizardStepWhatToDo from "./WizardStepWhatToDo";
import WizardStepActions from "./WizardStepActions";
import WizardStepSnapshotTiming from "./WizardStepSnapshotTiming";
import WizardStepSnapshot from "./WizardStepSnapshot";
import WizardStepAlert from "./WizardStepAlert";
import WizardStepRuleInfo from "./WizardStepRuleInfo";
import classes from "./RuleWizard.module.css";

// Step indices (fixed)
// 0: Trigger type (Gateway event / Switch status)
// 1: Trigger
// 2: Conditions / Location
// 3: Restrictions
// 4: Anything else? (Yes/No + AND/OR, not yet wired to the rule)
// 5: Deadline checks (other switches to check)
// 6: Deadline timeframe (only when there's a single trigger condition + at least one status check)
// 7: Day/time restrictions
// 8: What to do?
// 9: Actions          (only if wantOutputs)
// 10: Snapshot timing  (only if wantSnapshot)
// 11: Snapshot cameras (only if wantSnapshot)
// 12: Alert config     (only if wantAlert)
// 13: Rule Info

const RuleWizardInner = ({ allListOptionsArray, firmware, smartLocks, onClose, onSuccess, onFail }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const { state, dispatch } = useRule();

    const token = useSelector(s => s.auth.token);
    const client = useSelector(s => s.auth.client);
    const apiDispatch = useDispatch();

    useEffect(() => {
        dispatch({ type: "RESET" });
        dispatch({ type: "SET_SUPPORTS_ADV_RULES", payload: true });
    }, []);

    const goTo = (step) => {
        setCompletedSteps(prev => new Set([...prev, currentStep]));
        setCurrentStep(step);
    };

    const hasAbsenceCondition = state.conditions.some(c => c.is_absence_condition);
    const hasMultipleTriggerConditions = state.conditions.filter(c => !c.is_deadline_condition).length > 1;
    // With a single, non-absence trigger condition, the Timeframe step has nothing to show —
    // there's nothing to space out between conditions — so it's skipped entirely.
    const showTimeframeStep = !state.isTimeTrigger && (hasAbsenceCondition || hasMultipleTriggerConditions);

    // With more than one trigger condition (or an absence condition), the timeframe is already
    // set on the earlier Timeframe step. With just one trigger condition, that step has nothing
    // to show — so if the user adds a status check on the Deadline checks step, they need a
    // dedicated page afterwards to set the window it gets evaluated within.
    const needsDeadlineTimeframe = state.conditions.some(c => c.is_deadline_condition)
        && !hasAbsenceCondition
        && state.conditions.filter(c => !c.is_deadline_condition).length <= 1;

    const createRule = async (ruleState) => {
        console.log(ruleState);
        const conditionsWithRestrictions = ruleState.conditions.map(cond => {
            const { days_of_week: _omitDaysOfWeek, trigger_method: _omitTriggerMethod, trigger_method_type: _omitTriggerMethodType, ...condWithoutTimeTriggerFields } = cond;
            // Deadline checks are evaluated whenever the rule's actual trigger fires — day/week
            // restrictions and trigger-method fields belong to that trigger, not to a secondary status check.
            const skipTriggerFields = ruleState.isTimeTrigger || cond.is_deadline_condition;
            return {
                ...(skipTriggerFields ? condWithoutTimeTriggerFields : cond),
                ...(skipTriggerFields ? {} : { days_of_week: JSON.stringify(ruleState.days_of_week.map(Number)) }),
                ...(ruleState.time_window_start && ruleState.time_window_end ? {
                    time_window_start: ruleState.time_window_start,
                    time_window_end: ruleState.time_window_end,
                    time_condition: "DURING",
                } : {}),
            };
        });

        const triggerFields = ruleState.isTimeTrigger ? {
            type: "TIME",
            local_time: ruleState.triggerTime,
            days_of_week: ruleState.trigger_days_of_week.map(Number),
            timezone: ruleState.triggerTimezone,
        } : null;

        const body = ruleState.isGeofence
            ? {
                site_owner: String(ruleState.deviceUUID),
                name: String(ruleState.name),
                description: String(ruleState.description),
                conditions_logic_operator: "AND",
                is_enabled: true,
                conditions: conditionsWithRestrictions,
                actions: ruleState.actions,
                ...(triggerFields ? triggerFields : {}),
            }
            : {
                site_owner: String(ruleState.deviceUUID),
                name: String(ruleState.name),
                description: String(ruleState.description),
                evaluation_mode: ruleState.evaluation_mode,
                conditions_logic_operator: String(ruleState.conditions_logic_operator),
                is_enabled: true,
                conditions: conditionsWithRestrictions,
                actions: ruleState.actions,
                ...(ruleState.timeframe_window_ms ? (ruleState.evaluation_mode === "SEQUENCE" ? { sequence_timeout_ms: ruleState.timeframe_window_ms } : { timeframe_window_ms: ruleState.timeframe_window_ms, timeframe_window_mode: ruleState.timeframe_window_mode }) : {}),
                ...(ruleState.conditions.some((c) => c.is_deadline_condition) ? { deadline_logic_operator: String(ruleState.deadline_logic_operator) } : {}),
                ...(triggerFields ? { trigger: triggerFields } : {}),
            };
console.log(body);
        const response = await createAdvRuleApi({ token, client }, body, apiDispatch);
        console.log(response);
        if (response.success) {
            onClose();
            onSuccess();
        } else {
            onFail(response?.message);
        }
    };

    const DAY_NAMES = ["Sun", "Mon", "Tues", "Wed", "Thur", "Fri", "Sat"];

    const convertDayLabels = (days) => {
        const sorted = [...days].map(Number).sort((a, b) => a - b);

        if (sorted.length === 7) return "everyday";

        const isWeekday = (d) => d >= 1 && d <= 5;
        const isWeekend = (d) => d === 0 || d === 6;

        if (sorted.length === 5 && sorted.every(isWeekday)) return "weekdays";
        if (sorted.length === 2 && sorted.every(isWeekend)) return "weekends";

        return sorted.map(d => DAY_NAMES[d]).join(", ");
    }

    // Dynamic sidebar — only show steps that apply given current selections
    const GATEWAY_EVENT_PHRASES = {
        unlocked: "a correct pin entry",
        opened: "a padlock open",
        locked: "a padlock close",
        invalidPIN: "an invalid pin attempt",
    };

    const getSidebarConditionLabel = (cond) => {
        if (cond.condition_type === "GATEWAY_EVENT") {
            const phrase = GATEWAY_EVENT_PHRASES[cond.gateway_event_subtype] || cond.pin_name;
            return `${phrase} is reported by the gateway`;
        }

        const isLock = cond.is_lock ?? state.isLock;
        const isAbsence = cond.is_absence_condition;
        const isDeadline = cond.is_deadline_condition;
        const onLabel  = isLock ? "closed" : (cond.pin_status_on  || state.pinStatusOn  || "armed");
        const offLabel = isLock ? "open"   : (cond.pin_status_off || state.pinStatusOff || "disarmed");

        const absenceTimeSuffix = ` within ${state.timeframe_window_ms > 0 ? formatMsDuration(state.timeframe_window_ms) : "... time"}`;
        const timeSuffix = state.isTimeTrigger && state.triggerTime ? ` at ${state.triggerTime.slice(0, 5)}` : "";
        const stateStr = {
            ARMED:    isAbsence ? `is NOT ${onLabel} ${absenceTimeSuffix}` : isDeadline ? `is already ${onLabel}` : state.isTimeTrigger ? `is still ${onLabel}${timeSuffix}` : `changes status to ${onLabel}`,
            DISARMED: isAbsence ? `is NOT ${offLabel} ${absenceTimeSuffix}` : isDeadline ? `is already ${offLabel}` : state.isTimeTrigger ? `is still ${offLabel}${timeSuffix}` : `changes status to ${offLabel}`,
            ANY:      state.isTimeTrigger ? `is ${onLabel} or ${offLabel} (toggled)${timeSuffix}` : `changes status to ${onLabel} or ${offLabel} (toggled)`,
            PULSE:    `is pulsed${timeSuffix}`,
        }[cond.pin_state] ?? `is ${cond.pin_state || "..."}${timeSuffix}`;
        return `${cond.pin_name} ${stateStr}`;
    };

    const formatMsDuration = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        const seconds = ms / 1000;
        if (seconds <= 60) return `${seconds} seconds`;
        const minutes = seconds / 60;
        return `${minutes} minutes`;
    };

    const getTimeframeBetweenConditionsLabel = (cond) => {
        const timeRestriction = state.time_window_start_local != null && state.time_window_end_local != null ? `between ${state.time_window_start_local} and ${state.time_window_end_local}` : ""
        const daysRestriction = convertDayLabels(state.days_of_week)
        const hasAbsenceCondition = state.conditions.some(c => c.is_absence_condition);
        const timeframeRestriction = (!hasAbsenceCondition && state.timeframe_window_ms != null) ? `within ${formatMsDuration(state.timeframe_window_ms)} of eachother${state.timeframe_window_mode === "SLIDING" ? " (sliding)" : ""}` : ""
        return `${timeRestriction} ${daysRestriction} ${timeframeRestriction}`
    }

    const SNAPSHOT_PROVIDER_LABELS = { ajax: "Ajax", dahua: "Dahua", hikvision: "Hikvision" };

    const getSidebarSnapshotLabel = (camera) => {
        const name = camera.name ?? camera.cameraName ?? "Camera";
        const providerLabel = SNAPSHOT_PROVIDER_LABELS[camera.type] ?? camera.type;
        const channelText = camera.channel != null ? ` (Channel ${camera.channel})` : "";
        return providerLabel ? `${providerLabel} - ${name}${channelText}` : `${name}${channelText}`;
    };

    const getSidebarActionLabel = (pin) => {
        const action = state.actions.find(
            a => a.site_to === pin.DeviceUUID && a.target_output === String(pin.Pin)
        );
        console.log(action);
        const delayText = action.delay_seconds != 0 ? `after ${action.delay_seconds} seconds` : ""
        const durationText = action?.temporary_time_ms > 0 ? `for ${formatMsDuration(action.temporary_time_ms)}` : "";
        const typeLabels = {
            ARM: "Arm", TEMP_ARM: "Temp. Arm", DISARM: "Disarm",
            TEMP_DISARM: "Temp. Disarm", TOGGLE: "Toggle",
            PULSE: "Pulse", MATCH: "Match", OPPOSE: "Oppose",
        };
        const verb = action?.control_type ? (typeLabels[action.control_type] ?? action.control_type) : "";
        return verb ? `${pin.pinName} will ${verb} ${durationText} ${delayText}` : pin.pinName;
    };

    const getStepSummary = (step) => {
        switch (step) {
            case 0: {
                if (state.triggerType === "GATEWAY") return "Gateway event";
                if (state.triggerType === "SWITCH") return "Switch status";
                return null;
            }
            case 1: {
                if (state.isGeofence) return `Geofence · ${state.geofenceDirection}`;
                if (state.triggerType === "GATEWAY") {
                    const gatewayConditions = state.conditions.filter(c => c.condition_type === "GATEWAY_EVENT");
                    if (gatewayConditions.length === 0) return null;
                    if (gatewayConditions.length === 1) return gatewayConditions[0].pin_name;
                    return `${gatewayConditions.length} gateway events`;
                }
                if (state.isTimeTrigger) {
                    const timePart = state.triggerTime ? `Time · ${state.triggerTime.slice(0, 5)}` : "Time";
                    const pinPart = state.selectedRulePin?.pinName || state.pinName;
                    return pinPart ? `${timePart} · ${pinPart}` : timePart;
                }
                return state.selectedRulePin?.pinName || state.pinName || null;
            }
            case 2: {
                if (state.isGeofence) return `${state.geofenceDirection} zone`;
                const count = state.conditions.length;
                return count > 0 ? `${count} condition${count !== 1 ? "s" : ""}` : null;
            }
            case 3: {
                const dayCount = state.days_of_week.length;
                const hasTime = state.time_window_start && state.time_window_end;
                if (dayCount === 7 && !hasTime) return "Any time";
                const parts = [];
                if (dayCount < 7) parts.push(`${dayCount} day${dayCount !== 1 ? "s" : ""}`);
                if (hasTime) parts.push(`${state.time_window_start_local}–${state.time_window_end_local}`);
                return parts.join(" · ") || null;
            }
            case 4: {
                if (!state.conditions.some(c => c.is_deadline_condition)) return null;
                return state.deadline_logic_operator;
            }
            case 5: {
                const count = state.conditions.filter(c => c.is_deadline_condition).length;
                return count > 0 ? `${count} status check${count !== 1 ? "s" : ""}` : null;
            }
            case 6: {
                return state.timeframe_window_ms > 0 ? formatMsDuration(state.timeframe_window_ms) : null;
            }
            case 7: {
                const dayCount = state.days_of_week.length;
                const hasTime = state.time_window_start && state.time_window_end;
                if (dayCount === 7 && !hasTime) return "Any time";
                const parts = [];
                if (dayCount < 7) parts.push(`${dayCount} day${dayCount !== 1 ? "s" : ""}`);
                if (hasTime) parts.push(`${state.time_window_start_local}–${state.time_window_end_local}`);
                return parts.join(" · ") || null;
            }
            case 8: {
                const parts = [];
                if (state.wantOutputs) parts.push("Outputs");
                if (state.wantAlert) parts.push("Alert");
                if (state.wantSnapshot) parts.push("Snapshot");
                if (state.wantArcAlert) parts.push("ARC");
                return parts.join(" + ") || null;
            }
            case 9: {
                const pins = state.selectedPinsToAffect;
                if (pins.length === 0) return null;
                if (pins.length <= 2) return pins.map(p => p.pinName).join(", ");
                return `${pins.length} outputs`;
            }
            case 10: {
                const mode = state.snapshotTimingMode;
                if (mode === "BOTH") return `Immediate + ${state.snapshotDelaySeconds}s`;
                if (mode === "DELAY") return `After ${state.snapshotDelaySeconds}s`;
                return "Immediately";
            }
            case 11: {
                const cams = state.selectedSnapshotCameras;
                if (cams.length === 0) return null;
                if (cams.length === 1) return getSidebarSnapshotLabel(cams[0]);
                return `${cams.length} cameras`;
            }
            case 12: {
                return state.target_users ? "Recipients set" : null;
            }
            default:
                return null;
        }
    };

    const stepConfigs = [
        { step: 0, label: "Trigger type" },
        { step: 1, label: "Trigger" },
        { step: 2, label: state.isGeofence ? "Location" : "Additional Trigger Conditions" },
        ...(showTimeframeStep ? [{ step: 3, label: "Timeframe window" }] : []),
        { step: 4, label: "Anything else?" },
        { step: 5, label: "Other switches" },
        ...(needsDeadlineTimeframe ? [{ step: 6, label: "Timeframe end check" }] : []),
        { step: 7, label: "Restrictions" },
        { step: 8, label: "What to do?" },
        ...(state.wantOutputs  ? [{ step: 9, label: "Actions" }]  : []),
        ...(state.wantSnapshot ? [{ step: 10, label: "Snapshot timing" }] : []),
        ...(state.wantSnapshot ? [{ step: 11, label: "Snapshot cameras" }] : []),
        ...(state.wantAlert    ? [{ step: 12, label: "Alert" }]    : []),
        { step: 13, label: "Rule Info" },
    ];

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <WizardStepTriggerType
                        onNext={() => goTo(1)}
                    />
                );
            case 1:
                return (
                    <WizardStepTrigger
                        pins={allListOptionsArray}
                        firmware={firmware}
                        smartLocks={smartLocks}
                        onNext={() => goTo(2)}
                        onBack={() => goTo(0)}
                    />
                );
            case 2:
                return (
                    <WizardStepConditions
                        pins={allListOptionsArray}
                        smartLocks={smartLocks}
                        onNext={() => goTo(showTimeframeStep ? 3 : 4)}
                        onBack={() => goTo(1)}
                    />
                );
            case 3:
                return (
                    <WizardStepRestrictions
                        onNext={() => goTo(4)}
                        onBack={() => goTo(2)}
                    />
                );
            case 4:
                return (
                    <WizardStepAnythingElse
                        onNext={() => goTo(5)}
                        onSkip={() => goTo(7)}
                        onBack={() => goTo(showTimeframeStep ? 3 : 2)}
                    />
                );
            case 5:
                return (
                    <WizardStepDeadlineChecks
                        pins={allListOptionsArray}
                        smartLocks={smartLocks}
                        onNext={() => goTo(needsDeadlineTimeframe ? 6 : 7)}
                        onBack={() => goTo(4)}
                    />
                );
            case 6:
                return (
                    <WizardStepDeadlineTimeframe
                        onNext={() => goTo(7)}
                        onBack={() => goTo(5)}
                    />
                );
            case 7:
                return (
                    <WizardStepDayTimeRestrictions
                        onNext={() => goTo(8)}
                        onBack={() => goTo(needsDeadlineTimeframe ? 6 : 5)}
                    />
                );
            case 8:
                return (
                    <WizardStepWhatToDo
                        onNext={() => goTo(state.wantOutputs ? 9 : state.wantSnapshot ? 10 : state.wantAlert ? 12 : 13)}
                        onBack={() => goTo(7)}
                    />
                );
            case 9:
                return (
                    <WizardStepActions
                        pins={allListOptionsArray}
                        smartLocks={smartLocks}
                        onNext={() => goTo(state.wantSnapshot ? 10 : state.wantAlert ? 12 : 13)}
                        onBack={() => goTo(8)}
                    />
                );
            case 10:
                return (
                    <WizardStepSnapshotTiming
                        onNext={() => goTo(11)}
                        onBack={() => goTo(state.wantOutputs ? 9 : 8)}
                    />
                );
            case 11:
                return (
                    <WizardStepSnapshot
                        onNext={() => goTo(state.wantAlert ? 12 : 13)}
                        onBack={() => goTo(10)}
                    />
                );
            case 12:
                return (
                    <WizardStepAlert
                        onNext={() => goTo(13)}
                        onBack={() => goTo(state.wantSnapshot ? 11 : state.wantOutputs ? 9 : 8)}
                    />
                );
            case 13:
                return (
                    <WizardStepRuleInfo
                        onBack={() => goTo(state.wantAlert ? 12 : state.wantSnapshot ? 11 : state.wantOutputs ? 9 : 8)}
                        onCreate={createRule}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className={classes.wizardContainer}>
            <div className={classes.sidebar}>
                <span className={classes.sidebarTitle}>Create Rule</span>
                {stepConfigs.map((config, i) => {
                    const isActive = config.step === currentStep;
                    const isDone = completedSteps.has(config.step) && !isActive;
                    const summary = isDone ? getStepSummary(config.step) : null;
                    return (
                        <div
                            key={config.step}
                            className={`${classes.stepItem} ${isActive ? classes.stepItemActive : ""}`}
                        >
                            <div
                                className={`${classes.stepNumber} ${
                                    isActive ? classes.stepNumberActive : isDone ? classes.stepNumberDone : ""
                                }`}
                            >
                                {isDone ? "✓" : i + 1}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <span
                                    className={`${classes.stepLabel} ${
                                        isActive ? classes.stepLabelActive : isDone ? classes.stepLabelDone : ""
                                    }`}
                                >
                                    {config.label}
                                </span>
                                {summary && (
                                    <span className={classes.stepSummary}>{summary}</span>
                                )}
                            </div>
                        </div>
                    );
                })}
                {(() => {
                    const hasActions = state.isTimeTrigger || state.selectedPinsToAffect.length > 0 || state.wantAlert || state.wantSnapshot || state.wantArcAlert;
                    if (!state.conditions.length && !hasActions) return null;
                    const triggerConditions = state.conditions.filter(c => !c.is_deadline_condition);
                    const deadlineConditions = state.conditions.filter(c => c.is_deadline_condition);
                    return (
                        <div className={classes.sidebarSummary}>
                            {triggerConditions.length > 0 && (
                                <div className={classes.sidebarSummarySection}>
                                    <span className={classes.sidebarSummaryLabel}>Conditions</span>
                                    {triggerConditions.map((cond, i) => (
                                        <span key={i} style={{ display: "contents" }}>
                                            <span className={classes.sidebarChip} title={getSidebarConditionLabel(cond)}>
                                                {getSidebarConditionLabel(cond)}
                                            </span>
                                            {i < triggerConditions.length - 1 && (
                                                <span className={classes.sidebarChipThen}>
                                                    {state.evaluation_mode === "SEQUENCE" ? "THEN" : state.conditions_logic_operator}
                                                </span>
                                            )}
                                        </span>
                                    ))}
                                        {<span key={0} className={classes.sidebarChip}>
                                             {getTimeframeBetweenConditionsLabel(state.conditions)}
                                        </span>}
                                </div>
                            )}
                            {deadlineConditions.length > 0 && (
                                <div className={classes.sidebarSummarySection}>
                                    <span className={classes.sidebarSummaryLabel}>{`Status checks (${state.deadline_logic_operator})`}</span>
                                    {deadlineConditions.map((cond, i) => (
                                        <span key={i} style={{ display: "contents" }}>
                                            <span className={classes.sidebarChip} title={getSidebarConditionLabel(cond)}>
                                                {getSidebarConditionLabel(cond)}
                                            </span>
                                            {i < deadlineConditions.length - 1 && (
                                                <span className={classes.sidebarChipThen}>
                                                    {state.deadline_logic_operator}
                                                </span>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {hasActions && (
                                <div className={classes.sidebarSummarySection}>
                                    <span className={classes.sidebarSummaryLabel}>Actions</span>
                                    {state.isTimeTrigger && (
                                        <span className={classes.sidebarChipAction} title={`Triggers at ${state.triggerTime?.slice(0, 5)}`}>
                                            {`Triggers at ${state.triggerTime?.slice(0, 5) ?? "..."}`}
                                        </span>
                                    )}
                                    {state.selectedPinsToAffect.map((pin, i) => (
                                        <span key={i} className={classes.sidebarChipAction} title={getSidebarActionLabel(pin)}>
                                            {getSidebarActionLabel(pin)}
                                        </span>
                                    ))}
                                    {state.wantAlert    && <span className={classes.sidebarChipAction}>Send alert</span>}
                                    {state.wantSnapshot && (
                                        state.selectedSnapshotCameras.length > 0
                                            ? state.selectedSnapshotCameras.map((cam, i) => (
                                                <span key={`snapshot-${i}`} className={classes.sidebarChipAction} title={getSidebarSnapshotLabel(cam)}>
                                                    {getSidebarSnapshotLabel(cam)}
                                                </span>
                                            ))
                                            : <span className={classes.sidebarChipAction}>Take snapshot</span>
                                    )}
                                    {state.wantArcAlert && <span className={classes.sidebarChipAction}>ARC alert</span>}
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>
            <div className={classes.content}>
                {renderStep()}
            </div>
        </div>
    );
};

const RuleWizard = ({
    allListOptionsArray,
    firmware,
    smartLocks,
    setShowAutomation,
    setSuccessVisible,
    setFailVisible,
    setFailMessage,
    fetchAutomations,
}) => {
    const handleClose = () => setShowAutomation(null);
    const handleSuccess = () => {
        setSuccessVisible(true);
        if (fetchAutomations) fetchAutomations();
    };
    const handleFail = (message) => {
        if (setFailMessage) setFailMessage(message || "Something went wrong. Please try again.");
        setFailVisible(true);
    };

    return (
        <RuleProvider>
            <Dialog
                visible
                onHide={handleClose}
                style={{ width: "900px" }}
                header="Create Rule"
                modal
                closable
                pt={{ content: { style: { padding: 0 } } }}
            >
                <RuleWizardInner
                    allListOptionsArray={allListOptionsArray}
                    firmware={firmware}
                    smartLocks={smartLocks}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                    onFail={handleFail}
                />
            </Dialog>
        </RuleProvider>
    );
};

export default RuleWizard;
